import {Component, Injector, OnDestroy} from '@angular/core';

import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {ActivatedRoute} from '@angular/router';
import {Anchor} from '@layoutComponents/page-inside-navigation/anchor.interface';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {OrganizationService} from '../services/organization.service';
import {BusinessStructureProvider} from '@providers/business-structure/business-structure.provider';
import {TerritorialAuthorityProvider} from '@providers/territorial-authority/territorial-authority.provider';
import {LaboratoryDocumentsProvider} from '@providers/laboratory-documents/laboratory-documents.provider';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';


@Component({
    selector: 'organization-profile',
    templateUrl: './organization-profile.component.html',
    styleUrls: ['./organization-profile.component.scss']
})
export class OrganizationProfileComponent extends FrontendFormComponent implements OnDestroy {
    public anchors: Anchor[] = [
        {title: 'Данные организации', targetId: 'common', error: false},
        {title: 'Контактные данные', targetId: 'contacts', error: false},
        {title: 'Юридические реквизиты', targetId: 'legal', error: false},
        {title: 'Банковские реквизиты', targetId: 'bank', error: false}
    ];

    public afterSaveMessage = 'Данные организации успешно сохранены';

    public canViewDocuments = false;
    public canAddDocuments = false;

    private subs: Subscription[] = [];

    constructor(
        injector: Injector,
        private route: ActivatedRoute,
        public formService: OrganizationService,
        public businessStructures: BusinessStructureProvider,
        public territorialAuthorities: TerritorialAuthorityProvider,
        public laboratoryDocumentsProvider: LaboratoryDocumentsProvider,
        private authService: AuthService,
    ) {
        super(injector);
    }

    init() {
        this.form = this._fb.group({
            business_structure_id: [], // Форма собственности
            title: [], // Наименование
            short_title: [], // Сокращенное наименование
            full_name_signer: [], // Руководитель
            job_signer: [], // Должность
            job_signer_genitive: [], // Должность в родительном
            job_signer_dative: [], // Должность в дательном
            territorial_authority_id: [''], // Орган Ростеха
            place_of_incorporation: [], // Место регистрации
            incorporation_date: [], // Дата регистрации
            structure_file: this._fb.group({
                file_id: [],
                file_url: []
            }), // Структура подчинения организации
            file_blank: this._fb.group({
                // TODO Убрать префиксы file_ у объекта файла (требует работ по backend)
                file_id: [],
                file_url: []
            }), // Файл для шапки бланка
            new_legal_address_city_id: [], // Юридический адрес - город
            new_legal_address_city: [],
            legal_address_index: [], // Юридический адрес - индекс
            legal_address: [], // Юридический адрес - адрес
            new_postal_address_city_id: [], // Почтовый адрес
            new_postal_address_city: [],
            postal_address_index: [], //
            postal_address: [], //
            physical_address_city_id: [], // Фактический адрес
            physical_address_city: [],
            physical_address_index: [], //
            physical_address: [], //
            phone: [],
            fax: [],
            email: [],
            INN: [], // ИНН
            KPP: [], // КПП
            OKPO: [], // ОКПО
            OGRN: [], // ОГРН
            OKVED: [], // ОКВЕД
        });

        const {formValues} = this.route.snapshot.data;

        if (formValues) {
            this.setFormValues(formValues, this.form);
        }

        this.subs.push(
            this.form.statusChanges.pipe(
                debounceTime(100)
            ).subscribe(() => {
                this.updateAnchorsValidity();
            })
        );

        this.canViewDocuments = this.authService.hasPermissions([UserNamespace.Permission.structureUnitDocumentList]);
        this.canAddDocuments = this.authService.hasPermissions([UserNamespace.Permission.structureUnitDocumentCreate]);

        if (this.canViewDocuments) {
            this.anchors.push(
                {title: 'Документы организации', targetId: 'documents', error: false}
            );
        }
    }

    setAnchorErrorStatus(targetId: string, valid: boolean) {
        if (!targetId) {
            return;
        }

        const anchor = this.anchors.find(a => a.targetId === targetId);

        if (!anchor) {
            return;
        }

        anchor.error = !valid;

        this.anchors = [...this.anchors];
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    afterSave(data?) {
        super.afterSave(data);
        this.form.get('INN').disable();
    }

    private updateAnchorsValidity() {
        const common = [
            'business_structure_id',
            'title',
            'short_title',
            'full_name_signer',
            'job_signer',
            'job_signer_genitive',
            'job_signer_dative',
            'territorial_authority_id',
            'incorporation_date',
            'structure_file',
            'file_blank'
        ];
        const legal = [
            'place_of_incorporation',
            'INN',
            'KPP',
            'OKPO',
            'OGRN',
            'OKVED'
        ];
        const contacts = [
            'new_legal_address_city_id',
            'new_legal_address_city',
            'legal_address_index',
            'legal_address',
            'new_postal_address_city_id',
            'new_postal_address_city',
            'postal_address_index',
            'postal_address',
            'physical_address_city_id',
            'physical_address_city',
            'physical_address_index',
            'physical_address',
            'phone',
            'fax',
            'email'
        ];

        this.setAnchorErrorStatus('common', this.getValidStatusForFields(common));
        this.setAnchorErrorStatus('legal', this.getValidStatusForFields(legal));
        this.setAnchorErrorStatus('contacts', this.getValidStatusForFields(contacts));
    }

    private getValidStatusForFields(fields: string[]) {
        return fields.every(field => {
            return this.form.get(field).disabled || this.form.get(field).valid;
        });
    }
}
